import {BarChartRow} from "../charts/BarChart";
import {PieChartRow} from "../../../components/charts/PieChart";
import {MarketingConfig, UNITS_UNIT_OF_MEASURE_CODE} from "../data/MarketingConfig";
import {MarketingData} from "../data/MarketingData";
import {PercentageCircle} from "../../../components/charts/PercentageCircle";
import {BarChart2ValuesRow} from "../widgets/LargeHistogramChartWidget2Axis";
import UnitOfMeasure = MarketingConfig.UnitOfMeasure;
import MarketingDataConverter = MarketingData.MarketingDataConverter;
import BestProductSale = MarketingData.BestProductSale;

/**
 * Value mapper for the marketing tab.
 * Converts the data retrieved from the server to data used by the widgets.
 */
export class MarketingTabMapper {

    public static productSaleRowMapper(
        rows: MarketingData.ProductSaleRow[],
        unitOfMeasure: UnitOfMeasure,
    ): PieChartRow[] {
        return rows
            .map((row) => {
                return {
                    label: row.name,
                    frequency: MarketingDataConverter.chooseAndConvertValue(
                        row.quantity,
                        row.consumptions,
                        unitOfMeasure,
                    ),
                    color: row.color,
                };
            });
    }

    public static dayIntervalSaleRowMapper(
        rows: MarketingData.DayIntervalSaleRow[],
        unitOfMeasure: UnitOfMeasure,
    ): BarChartRow[] {
        return rows
            .sort((row1, row2) => row1.dayIntervalCode - row2.dayIntervalCode)
            .map((row) => {
                return {
                    name: row.name,
                    value: MarketingDataConverter.chooseAndConvertValue(row.quantity, row.consumptions, unitOfMeasure),
                    color: row.color,
                };
            });
    }

    public static weekdaySaleRowMapper(
        rows: MarketingData.WeekdaySaleRow[],
        unitOfMeasure: UnitOfMeasure,
    ): BarChartRow[] {
        return rows
            .sort((row1, row2) => row1.weekDay - row2.weekDay)
            .map((row) => {
                return {
                    name: row.name,
                    value: MarketingDataConverter.chooseAndConvertValue(row.quantity, row.consumptions, unitOfMeasure),
                    color: row.color,
                };
            });
    }

    public static monthSaleRowMapper(
        rows: MarketingData.MonthSaleRow[],
        unitOfMeasure: UnitOfMeasure,
    ): BarChart2ValuesRow[] {
        return rows
            .sort((row1, row2) => row1.date - row2.date)
            .map((row) => {
                return {
                    name: row.name,
                    value: MarketingDataConverter.chooseAndConvertValue(row.quantity, row.consumptions, unitOfMeasure),
                    value2: row.selections,
                    color: "#74C4DA",
                };
            });
    }

    public static weekSaleRowMapper(
        rows: MarketingData.WeekSaleRow[],
        unitOfMeasure: UnitOfMeasure,
    ): BarChart2ValuesRow[] {
        return rows
            .sort((row1, row2) => row1.date - row2.date)
            .map((row) => {
                return {
                    name: row.name,
                    value: MarketingDataConverter.chooseAndConvertValue(row.quantity, row.consumptions, unitOfMeasure),
                    value2: row.selections,
                    color: "#74C4DA",
                };
            });
    }

    public static productSelectionAverageMonthToDateMapper(
        rows: MarketingData.ProductSelectionsAverages[],
        unitOfMeasure: UnitOfMeasure,
    ): BarChart2ValuesRow[] {
        return rows
            .sort((row1, row2) =>
                MarketingDataConverter.chooseValue(
                    row2.selectionAverage, row2.selectionConsumptionsAverage, unitOfMeasure,
                ) -
                MarketingDataConverter.chooseValue(
                    row1.selectionAverage, row1.selectionConsumptionsAverage, unitOfMeasure,
                ),
            )
            .map((row) => {
                return {
                    name: row.name,
                    value: MarketingDataConverter.chooseAndConvertValue(
                        row.selectionAverage,
                        row.selectionConsumptionsAverage,
                        unitOfMeasure,
                    ),
                    value2: row.selections,
                    color: row.color,
                };
            });
    }

    public static getProductColor(productName: string, productSaleRows: MarketingData.ProductSaleRow[]): string {
        if (productSaleRows.length === 0) {
            return PercentageCircle.DEFAULT_COLOR;
        }
        const productsFound: MarketingData.ProductSaleRow[] = productSaleRows.filter(
            (element) => {
                return element.name === productName;
            },
        );
        if (productsFound.length === 0) {
            return PercentageCircle.DEFAULT_COLOR;
        }
        return productsFound[0].color;
    }

    public static codifyEntityName(name: string): string {
        return name.replace(/[^\wа-я0-9]/ig, "");
    }

    public static locationTrendRows(
        locationTrendRows: MarketingData.LocationTrendRow[],
        unitOfMeasure: UnitOfMeasure,
    ): MarketingData.BarChartGroup[] {
        return locationTrendRows
            .sort((row1, row2) => row1.total - row2.total)
            .map((elementRow: MarketingData.LocationTrendRow) => {
                return {
                    name: elementRow.code + " " + elementRow.description,
                    elements: [
                        {
                            name: elementRow.totalName,
                            color: elementRow.totalColor,
                            value: elementRow.total,
                        },
                        {
                            name: elementRow.comparativeTotalName,
                            color: elementRow.comparativeTotalColor,
                            value: elementRow.comparativeTotal,
                        },
                        {
                            name: elementRow.secondComparativeTotalName,
                            color: elementRow.secondComparativeTotalColor,
                            value: elementRow.secondComparativeTotal,
                        },
                    ],
                    id: elementRow.id,
                };
            });
    }

    public static weekDayDayIntervalSalesMapper(
        weekdayDayIntervalSales: MarketingData.WeekdayDayIntervalSale[],
        unitOfMeasure: UnitOfMeasure,
    ): MarketingData.BarChartGroup[] {
        return weekdayDayIntervalSales
            .sort((row1, row2) => row1.weekDay - row2.weekDay)
            .map((element: MarketingData.WeekdayDayIntervalSale) => {
                return {
                    name: element.name,
                    elements: element.dayIntervalSaleRows
                        .sort((row1, row2) => row1.dayIntervalCode - row2.dayIntervalCode)
                        .map((elementRow: MarketingData.DayIntervalSaleRow) => {
                                return {
                                    name: elementRow.name,
                                    color: elementRow.color,
                                    value: MarketingDataConverter.chooseAndConvertValue(
                                        elementRow.quantity,
                                        elementRow.consumptions,
                                        unitOfMeasure,
                                    ),
                                };
                            },
                        ),
                };
            });
    }

    public static assetsAverageRowMapper(
        assetsAverages: MarketingData.AssetsAverageRow[],
        locationAverageTitle: string,
        equipmentAverageTitle: string,
        selectionAverageTitle: string,
        unitOfMeasure: UnitOfMeasure,
    ): MarketingData.BarChartGroup[] {
        return assetsAverages
            .sort((row1, row2) => row1.date - row2.date)
            .map((element: MarketingData.AssetsAverageRow) => {
                const elements = [
                    {
                        name: locationAverageTitle,
                        color: "#F14954",
                        value: MarketingDataConverter.chooseAndConvertValue(
                            element.locationQuantity,
                            element.locationConsumptions,
                            unitOfMeasure,
                        ),
                    },
                    {
                        name: equipmentAverageTitle,
                        color: "#B7B8A8",
                        value: MarketingDataConverter.chooseAndConvertValue(
                            element.equipmentQuantity,
                            element.equipmentConsumptions,
                            unitOfMeasure,
                        ),
                    },
                ];
                if (!MarketingDataConverter.isUnitsUnitOfMeasure(unitOfMeasure)) {
                    elements.push(
                        {
                            name: selectionAverageTitle,
                            color: "#6BBF3F",
                            value: MarketingDataConverter.chooseAndConvertValue(
                                element.selectionQuantity,
                                element.selectionConsumptions,
                                unitOfMeasure,
                            ),
                        },
                    );
                }
                return {
                    name: element.name,
                    elements,
                };
            });
    }

    public static bestProduct(productSales: MarketingData.ProductSales,
                              unitOfMeasure: MarketingConfig.UnitOfMeasure): BestProductSale {
        const product = MarketingDataConverter.isUnitsUnitOfMeasure(unitOfMeasure) ?
            productSales.bestProductByConsumption : productSales.bestProductByQuantity;
        return {
            productName: product.productName,
            percentage: product.percentage,
            value: MarketingDataConverter.convert(product.value, unitOfMeasure),
        };
    }
}
